import { casesTableFieldsFragment } from "@lyfegen-platform/graphql-schema/fragments";
import gql from "graphql-tag";
import { preparationFieldsFragment, treatmentFieldsFragment } from "../../controllers/queries";

const agreementFieldsFragment = gql`
	${treatmentFieldsFragment}
	${preparationFieldsFragment}

	fragment BaseAgreementFieldsFragment on BaseAgreement {
		id
		subjectMatter
		pushedOn
		tags {
			id
			name
			type
		}
		canUserViewFinancials
		userOrganizationRole
		isAllowedFormsEnabled
		isRequiredFormsEnabled
		isAllowedRecordsEnabled
		isRequiredRecordsEnabled
		counterpartiesConnection {
			edges {
				node {
					name
					id
				}
			}
		}
		allowedFormFlexEntityTypes {
			id
			name
			localizationKey
		}
		requiredFormFlexEntityTypes {
			id
			name
			localizationKey
		}
		allowedRecordFlexEntityTypes {
			id
			name
			localizationKey
		}
		requiredRecordFlexEntityTypes {
			id
			name
			localizationKey
		}
		eligibilityConditionTreatments {
			...TreatmentFields
		}
		enrollingConditionSets {
			isIndicationNotEnrollingCondition
			isDiseaseNotEnrollingCondition
			conditions {
				rating
				therapyDecision
			}
			scopesConnection {
				edges {
					excludedOn
					node {
						scopeId
						code
						logicalOperator
						treatmentSets {
							generic {
								Name
								preparations {
									...PreparationFieldsFragment
								}
							}
							preparations {
								...PreparationFieldsFragment
							}
							treatments {
								...TreatmentFields
							}
							isFixedPriceTarget
						}
						diseases {
							Icd10Code
							Name
						}
						indications {
							id
							code
							type
							intent
							line
							mutation
							typeLocalizationKey
							intentLocalizationKey
							lineLocalizationKey
							mutationLocalizationKey
						}
						organizationScopesConnection {
							edges {
								organizationScopeId
								node {
									id
									name
									tenantId
								}
							}
						}
						countryScopesConnection {
							edges {
								countryScopeId
								node {
									name
								}
							}
						}
						isDeleted
					}
				}
			}
		}
		negotiableAttributes {
			id
			identifier
		}
		scenariosRelevantBrands {
			Name
		}
		isCoveringIntervalEnabled
		coveringFormWaitInterval
		scenarios(options: { sort: { order: ASC } }) {
			order
			operator
			isGrouped
			isProRating
			group
			hasMultipleBasePrices
			conditions(options: { sort: [{ order: ASC }] }) {
				order
				conditionType
				patientScope
				monitoringType
				deltaType
				treatmentDurationAspect
				discontinuationReason
				occurence
				intervention
				reintervention
				consideredSecInterventionRate
				uoMType
				rollingRangeType
				isNamedTimeRangeUsed
				consideredRating
				startingPoint
				timeframeRangeType
				timeframeRelation
				timeframeStart
				timeframeEnd
				countdownPoint
				comparedStartingPoint
				comparedTimeframeRangeType
				comparedTimeframeRelation
				comparedTimeframeStart
				comparedTimeframeEnd
				comparedCountdownPoint
				operator
				bothOperatorFirst
				bothOperatorSecond
				value
				endRangeValue
				brands {
					Name
				}
				treatments {
					...TreatmentFields
				}
				generics {
					Name
				}
				responseType
				responseInclusionType
				conditionStatus
				adverseEvents
				grades
				amountOfThresholdBasis
				article71Status
				patentStatus
				eventCategory
				eventSubCategories
				eventType
				eventFrequency
				eventResult
			}
			actions(options: { sort: [{ order: ASC }] }) {
				order
				actionType
				actionSet
				value
				priceType
				adHocPriceTypes
				itemScope
				isAdHocDiscount
				packOptionType
				entityPriceType
				rollingRangeType
				actionLogicalOperator
				uoMType
				ingredientRangeType
				ingredientStartItemNumber
				ingredientEndItemNumber
				isNamedTimeRangeUsed
				itemRangeType
				startItemNumber
				endItemNumber
				timeframeRangeType
				timeframeStart
				timeframeEnd
				startingPoint
				itemCombinationRule
				combinedItemTimeframeRangeType
				combinedItemTimeframeStart
				combinedItemTimeframeEnd
				itemCombinationRelation
				brandsConnection {
					edges {
						order
						node {
							Name
						}
					}
				}
				treatmentsConnection {
					edges {
						order
						node {
							...TreatmentFields
						}
					}
				}
				genericsConnection {
					edges {
						order
						node {
							Name
						}
					}
				}
				combinedItemBrands {
					Name
				}
				combinedItemTreatments {
					...TreatmentFields
				}
				combinedItemGenerics {
					Name
				}
				cycle {
					order
					name
				}
				recordNature
			}
		}
		currency {
			Code
			Name
		}
		treatmentPricesConnection {
			edges {
				price
				basePriceType
				node {
					...TreatmentFields
				}
			}
		}
		validFromDate
		validToDate
		dataContainers {
			timeframeStart
			timeframeEnd
			timeframeStartingPoint
			timeframeRangeType
			type
			subType
			isStartingPoint
			entityTypeName
		}
		eligibilityConditionSetsConnection {
			edges {
				node {
					code
				}
				value
				numberOfAdministeredTreatmentsOperator
				minimumPercentage
				days
				previousTestNumber
				referenceTestRangeDays
				referenceAdministrationSequenceNumber
				numberOfAdministeredRangeDays
				numberOfAdministeredTreatments
				cycleAdherence
			}
		}
	}
`;

export const GET_CASE_BY_ID = gql`
	${agreementFieldsFragment}
	${treatmentFieldsFragment}

	query GetCaseById($id: ID!) {
		cases(where: { id: $id, isDeleted: false }) {
			id
			status
			sourceTenantId
			actualAgreementId
			documentStatus
			canUserViewFinancials
			agreementUserRole
			creationDate
			flexEntitiesConnection(where: { node: { containerType: FORM, isDeleted: false } }) {
				edges {
					isStartingPoint
					node {
						id
						uuid
						status
						occurrence
						data
						documentStatus
						refundStatus
						createdOn
						lastUpdatedOn
						firstPrescriptionDate
						createdBy {
							id
							tenantId
							email
							isDeleted
							firstName
							lastName
						}
						type {
							id
							idPrefix
							localizationKey
							name
							features
							data
							isStartingPoint
							ignoredAttributes
							catalogueAttributesConnection {
								edges {
									isDisplayedInCaseDetails
									isDisplayedInCaseTable
									node {
										id
										identifier
										dataType
										dataSubType
										caption
									}
								}
							}
						}
						cases {
							id
						}
					}
				}
			}
			financialDataPoints {
				uuid
				isLast
				isDeleted
				isDelta
				revokedOn
				date
				documentStatus
				prescriptionDate
				refundDeltaNumber
				failReason
				treatment {
					...TreatmentFields
				}
				quantity
				payable
				variable
				refundable
				payablePercentage
				variablePercentage
				refundablePercentage
				basePrice
				evaluationSnapshot
				sourceRefund(where: { isDeleted: false }) {
					id
					status
					adjustedAmount
					amount
					adjustedRate
					creatableDocument
					refundRate
					adjustmentComment
					lastAdjustedAt
					adjustedBy {
						id
						firstName
						lastName
					}
					documentRefundGroupId
					documents(where: { status_IN: [ISSUED, SETTLED] }) {
						id
						status
					}
					isAgreementBuyer
					isAgreementSeller
					documentFinancialDataPointConnection(
						where: { edge: { OR: [{ NOT: { status: REMOVED } }, { status_IS_NULL: true }] } }
					) {
						edges {
							status
							smartMipStatus
							node {
								uuid
							}
						}
					}
				}
				sourceDocumentState {
					id
					isConfirmedBySeller
					isConfirmedByBuyer
					isConfirmedByExternalParty
					isSentForExternalReview
					isAgreementBuyer
					isAgreementSeller
					status
					type
				}
				evaluates {
					successor {
						id
					}
					isRevoked
					status
				}
			}
			baseAgreement {
				...BaseAgreementFieldsFragment
				nextVersions {
					...BaseAgreementFieldsFragment
				}
			}
			legalEntity {
				name
			}
			patient {
				insuranceId
				initials
				yearOfBirth
			}
			indication {
				id
				code
				type
				intent
				line
				mutation
				typeLocalizationKey
				intentLocalizationKey
				lineLocalizationKey
				mutationLocalizationKey
			}
			disease {
				Icd10Code
				Name
			}
			relevantArticle71Indication {
				id
				name
			}
			isFormOwner
			assignedTo {
				id
				email
				firstName
				lastName
				tenantId
			}
			negotiationRounds {
				id
				changedOn
				data
				status
				source {
					tenantId
					createdOn
					type {
						id
						ignoredAttributes
						containerType
						localizationKey
					}
				}
				proposedBy {
					id
					name
				}
			}
			counterpartyInReview
		}
	}
`;

export const GET_CASES_FILTERS = gql`
	query GetCasesFilters {
		GetCasesTableSourceFilterData {
			patientIds
			agreementNames
			brandNames
			statuses
			inReviewByNames
			suspensionReasons
			amountRanges {
				start
				end
				count
			}
			initials
			ids
			indicationCodes
		}
		filters: GetUserContextFiltersCases
	}
`;
export const UPDATE_CASES_FILTER = gql`
	mutation UpdateUserContextFiltersCases($req: String) {
		UpdateUserContextFiltersCases(req: $req)
	}
`;

export const GET_CASES = gql`
	${casesTableFieldsFragment}

	query GetCases($where: CaseTableRowWhere!, $options: CaseTableRowOptions) {
		caseTableRows(where: $where, options: $options) {
			...GetCasesFields
		}

		filteredCaseCount: caseTableRows(where: $where) {
			_count
		}

		allCaseCount: caseTableRows {
			_count
		}
	}
`;

export const GET_FILTERED_CASE_COUNT = gql`
	query GetFilteredCaseCount($where: CaseTableRowWhere!) {
		filteredCaseCount: caseTableRows(where: $where) {
			_count
		}
	}
`;

export const CASES_SUBSCRIPTION = gql`
	subscription OnCaseCreated {
		caseCreated
	}
`;

export const GET_USERS = gql`
	query GetAvailableCaseAssigneesQuery($caseId: ID!) {
		GetAvailableCaseAssignees(caseId: $caseId) {
			id
			email
			firstName
			lastName
		}
	}
`;

export const GET_COUNTERPARTIES_IN_REVIEW = gql`
	query GetCounterpartiesInReviewQuery {
		GetCounterpartiesInReview
	}
`;

export const UPDATE_CASE_ASSIGNEE = gql`
	mutation UpdateCaseAssignee($input: UpdateCaseAssigneeInput!) {
		UpdateCaseAssignee(req: $input) {
			id
		}
	}
`;

export const UPDATE_NEGOTIATION_ROUND = gql`
	mutation UpdateNegotiationRound($input: NegotiationRoundInput!) {
		UpdateNegotiationRound(input: $input) {
			id
			status
			changedOn
			data
			proposedBy {
				id
				name
			}
			case {
				status
			}
		}
	}
`;

export const GET_SIMILAR_CASES_BY_CASE_ID = gql`
	query GetSimilarCasesByCaseId($caseId: ID!, $diseaseIcd10Code: String) {
		GetSimilarCases(caseId: $caseId, diseaseIcd10Code: $diseaseIcd10Code) {
			creationDate
			id
			formattedRefundRate
			rating
		}
	}
`;

export const GET_CASE_EVENT_LOGS_BY_CASE_ID = gql`
	query GetSanitizedCaseEventLogs($caseId: ID!) {
		GetSanitizedCaseEventLogs(caseId: $caseId) {
			eventCategory
			eventType
			trigger
			triggeredOn
			data
		}
	}
`;

export const GET_CASE_EVENT_LOG_FILTERS = gql`
	query GetUserContextFiltersCaseEventLogs {
		filters: GetUserContextFiltersCaseEventLogs
	}
`;

export const UPDATE_CASE_EVENT_LOG_FILTER = gql`
	mutation UpdateUserContextFiltersCaseEventLogs($req: String) {
		UpdateUserContextFiltersCaseEventLogs(req: $req)
	}
`;
